import { ROUTES } from '@/shared/config';
import { useTranslation } from '@/shared/lib/common/i18n';
import { claimChunkErrorReload, isChunkLoadError } from '@/shared/lib/util/chunkLoadError';
import { isProd } from '@/shared/lib/util/environment';
import { theme } from '@/shared/styles';
import { Body1, H4 } from '@/shared/ui/components/Typography';
import { Box, Button, CssBaseline, Stack } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { useEffect, useState } from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router-dom';

const errorDetail = (error: unknown): string => {
  if (isRouteErrorResponse(error)) return `${error.status} ${error.statusText}`;
  if (error instanceof Error) return error.message;
  return String(error);
};

/**
 * The root route's `errorElement`. It replaces `AppRoot` when anything below it throws, so it sits
 * outside `Providers` and brings its own theme.
 *
 * A chunk load error (a tab still on the previous build after a deploy) reloads once on its own,
 * which picks up the new build and usually lands the user on the page they asked for. If that
 * already happened moments ago, the chunk is really missing, so it shows a Refresh screen instead
 * of reloading again.
 */
const RouteErrorPage = () => {
  const { t } = useTranslation();
  const error = useRouteError();
  const chunkError = isChunkLoadError(error);

  // Decided once per mount: claiming the reload writes to sessionStorage, so re-running it on a
  // re-render would read its own write and flip to the Refresh screen mid-navigation.
  const [autoReloading] = useState(() => chunkError && claimChunkErrorReload());

  useEffect(() => {
    if (autoReloading) window.location.reload();
  }, [autoReloading]);

  if (autoReloading) return null;

  const title = chunkError ? t.common.errorPage.newVersionTitle : t.common.errorPage.genericTitle;
  const message = chunkError ? t.common.errorPage.newVersionMessage : t.common.errorPage.genericMessage;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Stack sx={{ minHeight: '100vh', alignItems: 'center', justifyContent: 'center', gap: 2, padding: 3, textAlign: 'center' }}>
        <H4>{title}</H4>
        <Body1>{message}</Body1>
        <Stack direction="row" sx={{ gap: 1 }}>
          <Button variant="contained" onClick={() => window.location.reload()}>
            {t.common.actions.refresh}
          </Button>
          {/* A full page load rather than navigate(): the router that just failed may be in a bad state. */}
          {!chunkError && (
            <Button variant="outlined" onClick={() => window.location.assign(ROUTES.HOME)}>
              {t.common.errorPage.goHome}
            </Button>
          )}
        </Stack>
        {/* The error message only, never the stack, and never in prod. */}
        {!isProd() && (
          <Box component="pre" sx={{ maxWidth: '100%', whiteSpace: 'pre-wrap', fontSize: 12, opacity: 0.7 }}>
            {errorDetail(error)}
          </Box>
        )}
      </Stack>
    </ThemeProvider>
  );
};

export default RouteErrorPage;
